<template>
  <div class="card listing-card">
    <form @submit.prevent class="needs-validation" novalidate>
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{serverError}}
      </div>
      <div class="card-body">
        <div class="label-text">
          <span v-if="!partnershipGrant.id">Add Partnership Grant</span>
          <span v-if="partnershipGrant.id">Edit Partnership Grant</span>
        </div>
        <hr/>
        <div class="form-group row">
          <label for="grantProgram" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Grant Program
          </label>
          <div class="loading col-lg-5" v-if="grantProgramOptionsLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!grantProgramOptionsLoading"
                    v-validate="'required'"
                    data-vv-as="Grant Program"
                    class="form-control"
                    name="grantProgram"
                    v-model="partnershipGrant.grantProgramId">
              <option value="" disabled>Select Grant Program</option>
              <option v-for="option in grantProgramOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('grantProgram') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            <span class="text-danger">*</span>Grantor
          </label>
          <div class="col-lg-5 mb-2">
            <div>
              {{ partnershipGrant.grantor | contactNameOrDefault }}
            </div>
            <div v-if="grantorError" class="invalid-feedback d-block mb-2">
              Grantor is required. Please use Search Contacts to select a Grantor.
            </div>
          </div>
          <div class="col-lg-3">
            <SearchContactsModal
              :modalId="'contactSearch-grantor-' + partnershipGrant.id"
              @set-contact="setGrantor" />
          </div>
        </div>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            <span class="text-danger">*</span>Grantee
          </label>
          <div class="col-lg-5 mb-2">
            <div>
              {{ partnershipGrant.grantee | contactNameOrDefault }}
            </div>
            <div v-if="granteeError" class="invalid-feedback d-block mb-2">
              Grantee is required. Please use Search Contacts to select a Grantee.
            </div>
          </div>
          <div class="col-lg-3">
            <SearchContactsModal
              :modalId="'contactSearch-grantee-' + partnershipGrant.id"
              @set-contact="setGrantee" />
          </div>
        </div>
        <div class="form-group row">
          <label for="awardAmount" class="col-lg-4 col-form-label label-text">
            <span class="text-danger">*</span>Grant Award Amount
          </label>
          <div class="col-lg-8">
            <money data-vv-as="Grant Award Amount"
                   v-validate="'min_money:0.01'"
                   v-model="partnershipGrant.awardAmount"
                   v-bind="moneyFormat"
                   type="text"
                   class="form-control"
                   name="awardAmount"
                   placeholder="Enter Award Amount"
                   maxlength="30" />
              <div class="invalid-feedback" style="display:block">
                {{ errors.first('awardAmount') }}
              </div>
          </div>
        </div>
        <div class="form-group row">
          <label for="partnershipGrantNotes" class="col-lg-4 col-form-label label-text">
            Grant Notes
          </label>
          <div class="col-lg-8">
            <textarea data-vv-as="Notes"
                      type="textarea"
                      class="form-control"
                      name="notes"
                      v-model="partnershipGrant.notes"
                      placeholder="Enter Notes"
                      rows="2"
                      maxlength="256" />
              <small v-if="!errors.first('notes')" class="form-text text-muted">
                Max 256 characters
              </small>
              <div class="invalid-feedback">{{ errors.first('notes') }}</div>
          </div>
        </div>
      </div>
      <div class='card-footer right-align-content'>
        <b-button
          :disabled="submitting"
          variant="danger" class="button-row-spacing"
          v-b-modal="'cancelPartnershipGrantConfirmation-' + partnershipGrant.id">
          Cancel
        </b-button>
        <b-button
          :disabled="submitting"
          @click="submitWrapper(partnershipGrant)">Save</b-button>
      </div>
    </form>
    <CancelModal
      :modalId="'cancelPartnershipGrantConfirmation-' + partnershipGrant.id"
      @cancelled="cancelEdit">
    </CancelModal>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { Money } from 'v-money';
import CancelModal from '../CancelModal.vue';
import FormSubmit from '../../mixins/FormSubmit';
import SearchContactsModal from '../contacts/SearchContactsModal.vue';
import { EntityModules, LookupModules } from '@/store/moduleConfig';

export default {
  name: 'PartnershipGrantForm',
  mixins: [FormSubmit],
  components: {
    CancelModal,
    Money,
    SearchContactsModal,
  },
  computed: {
    ...mapState(LookupModules.GRANT_PROGRAMS.namespace, {
      grantProgramOptionsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.PARTNERSHIP_GRANT.namespace, {
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.GRANT_PROGRAMS.namespace, {
      grantProgramOptions: 'getActiveOptions',
    }),
  },
  directives: { money: Money },
  props: [
    'partnershipGrant',
    'existingPartnershipGrant',
  ],
  data() {
    return {
      grantorError: false,
      granteeError: false,
      validationAttempted: false,
      disabledDateConfig: {
        disabledDates: {
          from: new Date(),
        },
      },
      moneyFormat: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false,
      },
    };
  },
  methods: {
    cancelEdit() {
      this.$emit('closeForm', this.partnershipGrant.id);
    },
    submitWrapper(entity) {
      this.checkContactErrors();
      this.onSubmit(
        entity,
        EntityModules.PARTNERSHIP_GRANT.namespace,
        [
          this.grantorError,
          this.granteeError,
        ],
        // this.$emit('closeForm', entity.id),
      );
      this.validationAttempted = true;
    },
    setGrantor(contactResult) {
      this.partnershipGrant.grantorId = contactResult.crmId;
      this.partnershipGrant.grantor = contactResult;
      this.checkGrantorError();
    },
    setGrantee(contactResult) {
      this.partnershipGrant.granteeId = contactResult.crmId;
      this.partnershipGrant.grantee = contactResult;
      this.checkGranteeError();
    },
    checkContactErrors() {
      this.checkGrantorError();
      this.checkGranteeError();
    },
    checkGranteeError() {
      this.granteeError = !this.partnershipGrant.granteeId;
    },
    checkGrantorError() {
      this.grantorError = !this.partnershipGrant.grantorId;
    },
  },
};
</script>
